import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, DialogContentText, TextField, Select, MenuItem, FormControl,
  InputLabel, Alert, CircularProgress, Tooltip, IconButton, Divider,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import axios from 'axios';
import authService from '../../services/authService';

const API_URL = process.env.REACT_APP_API_URL || '';

interface TeamMember {
  user_id: number;
  email: string;
  name: string | null;
  role: string;
  is_owner: boolean;
  invited_at: string | null;
  invited_by: number | null;
  last_login: string | null;
}

interface LabAccess {
  lab_id: string;
  name: string;
  role: string;
  is_owner: boolean;
  permissions: string[];
}

interface RoleOption {
  name: string;
  description: string | null;
  is_system: boolean;
  permissions: string[];
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

/**
 * Team management for a vendor account.
 *
 * A vendor may manage several labs and access is per-lab, so everything here
 * hangs off the selected lab. The account owner (labs.vendor_id) is shown with
 * an Owner badge and cannot be edited or removed — they have no membership row
 * to change.
 */
const TeamManager: React.FC = () => {
  const getHeaders = () => authService.getAuthHeaders();

  const [labs, setLabs] = useState<LabAccess[]>([]);
  const [selectedLab, setSelectedLab] = useState<string>('');
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'member' });
  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);

  const currentLab = labs.find((l) => l.lab_id === selectedLab);
  const can = (perm: string) => !!currentLab?.permissions.includes(perm);

  // Pull the error message the API actually sent, not axios's generic one.
  const apiError = (e: any, fallback: string) =>
    e?.response?.data?.detail || e?.response?.data?.error || e?.message || fallback;

  const loadAccess = useCallback(async () => {
    try {
      const [accessRes, rolesRes] = await Promise.all([
        axios.get(`${API_URL}/api/v1/lis-portal/me/access`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/v1/lis-portal/team-roles`, { headers: getHeaders() }),
      ]);
      const accessible: LabAccess[] = accessRes.data?.labs || [];
      setLabs(accessible);
      setRoles(rolesRes.data || []);
      setSelectedLab((prev) => prev || accessible[0]?.lab_id || '');
    } catch (e: any) {
      setError(apiError(e, 'Failed to load your account access'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTeam = useCallback(async (labId: string) => {
    if (!labId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/v1/lis-portal/team`, {
        headers: getHeaders(),
        params: { lab_id: labId },
      });
      setMembers(res.data || []);
    } catch (e: any) {
      setMembers([]);
      setError(apiError(e, 'Failed to load the team for this lab'));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { loadAccess(); }, [loadAccess]);
  useEffect(() => { if (selectedLab) loadTeam(selectedLab); }, [selectedLab, loadTeam]);

  const handleInvite = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/v1/lis-portal/team/invite`,
        { email: inviteForm.email, name: inviteForm.name || null, role: inviteForm.role, send_email: true },
        { headers: getHeaders(), params: { lab_id: selectedLab } },
      );
      setNotice(`Invited ${inviteForm.email} as ${inviteForm.role}. They've been emailed sign-in details.`);
      setInviteOpen(false);
      setInviteForm({ email: '', name: '', role: 'member' });
      await loadTeam(selectedLab);
    } catch (e: any) {
      setError(apiError(e, 'Failed to send the invite'));
    } finally {
      setBusy(false);
    }
  };

  const handleRoleChange = async (member: TeamMember, role: string) => {
    setBusy(true);
    setError(null);
    try {
      await axios.patch(
        `${API_URL}/api/v1/lis-portal/team/${member.user_id}`,
        { role },
        { headers: getHeaders(), params: { lab_id: selectedLab } },
      );
      setNotice(`${member.email} is now ${role}.`);
      await loadTeam(selectedLab);
    } catch (e: any) {
      setError(apiError(e, 'Failed to change that role'));
      await loadTeam(selectedLab);  // resync the select back to the real value
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setBusy(true);
    setError(null);
    try {
      await axios.delete(`${API_URL}/api/v1/lis-portal/team/${removeTarget.user_id}`, {
        headers: getHeaders(),
        params: { lab_id: selectedLab },
      });
      setNotice(`Removed ${removeTarget.email} from ${currentLab?.name || selectedLab}.`);
      setRemoveTarget(null);
      await loadTeam(selectedLab);
    } catch (e: any) {
      setError(apiError(e, 'Failed to remove that member'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!labs.length) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="info">
          Your account isn't linked to any labs yet, so there's no team to manage.
        </Alert>
      </Box>
    );
  }

  const canView = can('vendor.team.view');

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>Team</Typography>
          <Typography variant="body2" color="text.secondary">
            People who can sign in to this vendor account. Each person gets their own
            login, so uploads and report downloads are attributable to a named user.
          </Typography>
        </Box>
        {canView && can('vendor.team.invite') && (
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setInviteOpen(true)}
            disabled={busy}
          >
            Invite Member
          </Button>
        )}
      </Box>

      <FormControl sx={{ minWidth: 320, my: 2 }} size="small">
        <InputLabel id="team-lab-label">Lab</InputLabel>
        <Select
          labelId="team-lab-label"
          label="Lab"
          value={selectedLab}
          onChange={(e) => setSelectedLab(e.target.value as string)}
        >
          {labs.map((l) => (
            <MenuItem key={l.lab_id} value={l.lab_id}>
              {l.name} ({l.lab_id}){l.is_owner ? ' — you own this' : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {!canView ? (
        <Alert severity="info">
          Your role on this lab ({currentLab?.role}) doesn't include team management.
          Ask an admin on the account if you need access.
        </Alert>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Added</TableCell>
                  <TableCell>Last sign-in</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.user_id} hover>
                    <TableCell>
                      {m.name || '—'}
                      {m.is_owner && (
                        <Tooltip title="Account owner — always an admin, cannot be changed or removed">
                          <Chip
                            size="small"
                            icon={<StarIcon />}
                            label="Owner"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{m.email}</TableCell>
                    <TableCell>
                      {m.is_owner || !can('vendor.team.change_role') ? (
                        <Chip size="small" label={m.role} />
                      ) : (
                        <Select
                          size="small"
                          value={m.role}
                          disabled={busy}
                          onChange={(e) => handleRoleChange(m, e.target.value as string)}
                          sx={{ minWidth: 120 }}
                        >
                          {roles.map((r) => (
                            <MenuItem key={r.name} value={r.name}>{r.name}</MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>{fmtDate(m.invited_at)}</TableCell>
                    <TableCell>{fmtDate(m.last_login)}</TableCell>
                    <TableCell align="right">
                      {!m.is_owner && can('vendor.team.remove') && (
                        <Tooltip title="Remove from this lab">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={busy}
                              onClick={() => setRemoveTarget(m)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!members.length && !busy && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No team members yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {busy && <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
        </Paper>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite a team member</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            They'll be added to <strong>{currentLab?.name}</strong> and emailed sign-in
            details. If they're new, the email includes a temporary password they'll be
            asked to change on first login.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Email"
            type="email"
            value={inviteForm.email}
            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Name (optional)"
            value={inviteForm.name}
            onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="invite-role-label">Role</InputLabel>
            <Select
              labelId="invite-role-label"
              label="Role"
              value={inviteForm.role}
              onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as string })}
            >
              {roles.map((r) => (
                <MenuItem key={r.name} value={r.name}>
                  {r.name}{r.description ? ` — ${r.description}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            {inviteForm.role === 'admin'
              ? 'Admins can manage the team, edit lab settings and integrations, and view and rotate this account\'s API keys and webhook secrets.'
              : 'Members can upload and process input files, download output reports, and view lab and report status. They cannot see API keys or webhook secrets, or change lab settings.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={busy || !inviteForm.email.trim()}
          >
            Send Invite
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove confirmation */}
      <Dialog open={!!removeTarget} onClose={() => setRemoveTarget(null)}>
        <DialogTitle>Remove team member?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{removeTarget?.email}</strong> will lose access to{' '}
            <strong>{currentLab?.name}</strong> immediately. Their account and any access
            to your other labs stay intact.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRemove} disabled={busy}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamManager;
